import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Printer, Download, Search } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ReportChartRow, BarCard, asSeries } from './ReportCharts';
import { isoDate } from '../../utils/time';

const fmtAmt  = v => `$${parseFloat(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMet  = (v, metric) => {
  const n = parseFloat(v || 0);
  if (metric === 'hours') return n % 1 === 0 ? String(n) : n.toFixed(1);
  if (metric === 'mileage') return n.toFixed(1);
  return String(Math.round(n));
};

const METRIC_LABEL = { hours: 'Hours', mileage: 'Mileage', expenses: 'Expenses' };

export default function DeliveredVsInvoiced() {
  const today = new Date();
  const [startDate, setStart] = useState(format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd'));
  const [endDate,   setEnd]   = useState(format(today, 'yyyy-MM-dd'));
  const [metric,    setMetric]  = useState('hours');
  const [search,    setSearch]  = useState('');
  const [data,      setData]    = useState(null);
  const [loading,   setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate, metric });
    api.get(`/reports/delivered-vs-invoiced?${p}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, metric]);

  useEffect(() => { load(); }, [load]);

  function applyPreset(preset) {
    const now = new Date(); const y = now.getFullYear(); const m = now.getMonth();
    const iso = isoDate;
    if (preset === 'this-month')   { setStart(iso(new Date(y, m, 1)));     setEnd(iso(now)); }
    else if (preset === 'last-month')   { setStart(iso(new Date(y, m-1, 1))); setEnd(iso(new Date(y, m, 0))); }
    else if (preset === 'this-quarter') { const q = Math.floor(m/3)*3; setStart(iso(new Date(y, q, 1))); setEnd(iso(now)); }
    else if (preset === 'this-year')    { setStart(iso(new Date(y, 0, 1))); setEnd(iso(now)); }
    else if (preset === 'this-fy')      { const fyY = m >= 6 ? y : y-1; setStart(iso(new Date(fyY, 6, 1))); setEnd(iso(now)); }
  }

  function downloadCSV() {
    const rows = data?.rows;
    if (!rows?.length) return;
    const ml = METRIC_LABEL[metric];
    const cols = ['Client','Delivered Amount','Invoiced Amount','Cancelled Amount','Non Invoiced Amount',
      `Delivered ${ml}`,`Invoiced ${ml}`,`Cancelled ${ml}`,`Non Invoiced ${ml}`];
    const makeRow = r => [r.client, r.delivered_amount, r.invoiced_amount, r.cancelled_amount,
      r.non_invoiced_amount, r.delivered_metric, r.invoiced_metric, r.cancelled_metric, r.non_invoiced_metric];
    const lines = [cols, ...rows.map(makeRow)];
    if (data.totals) lines.push(makeRow(data.totals));
    const csv = lines.map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `delivered_vs_invoiced_${startDate}_${endDate}.csv`;
    a.click();
  }

  const allRows = data?.rows || [];
  const rows   = search ? allRows.filter(r => r.client?.toLowerCase().includes(search.toLowerCase())) : allRows;
  const totals = data?.totals || null;
  const ml     = METRIC_LABEL[metric];

  const cols = [
    { key: 'client',              label: 'Client',                  amt: false },
    { key: 'delivered_amount',    label: 'Delivered Amount',        amt: true  },
    { key: 'invoiced_amount',     label: 'Invoiced Amount',         amt: true  },
    { key: 'cancelled_amount',    label: 'Cancelled Amount',        amt: true  },
    { key: 'non_invoiced_amount', label: 'Non Invoiced Amount',     amt: true  },
    { key: 'delivered_metric',    label: `Delivered ${ml}`,         amt: false },
    { key: 'invoiced_metric',     label: `Invoiced ${ml}`,          amt: false },
    { key: 'cancelled_metric',    label: `Cancelled ${ml}`,         amt: false },
    { key: 'non_invoiced_metric', label: `Non Invoiced ${ml}`,      amt: false },
  ];

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div>
        <p className="text-sm text-slate-500 font-medium">Report</p>
        <h1 className="text-2xl font-bold text-slate-800">Delivered Vs Invoiced</h1>
      </div>

      {/* Charts */}
      {totals && allRows.length > 0 && (
        <ReportChartRow>
          <BarCard title="Delivered vs invoiced ($)" data={[
            { name: 'Delivered', value: Number(totals.delivered_amount) || 0 },
            { name: 'Invoiced', value: Number(totals.invoiced_amount) || 0 },
            { name: 'Cancelled', value: Number(totals.cancelled_amount) || 0 },
            { name: 'Non-invoiced', value: Number(totals.non_invoiced_amount) || 0 },
          ]} />
          <BarCard title="Top non-invoiced ($)" subtitle="Revenue not yet billed" data={asSeries(allRows, 'client', 'non_invoiced_amount', { limit: 10 })} color="#EF4444" horizontal />
        </ReportChartRow>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        {/* Controls */}
        <div className="flex flex-wrap justify-between items-center gap-3 px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-3 flex-wrap">
            <select value={metric} onChange={e => setMetric(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-400">
              <option value="hours">Hours</option>
              <option value="mileage">Mileage</option>
              <option value="expenses">Expenses</option>
            </select>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search client…"
                className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 w-40"/>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {[['this-month','This Month'],['last-month','Last Month'],['this-quarter','Quarter'],['this-year','This Year'],['this-fy','This FY']].map(([key, label]) => (
              <button key={key} onClick={() => applyPreset(key)}
                className="px-2.5 py-1 text-xs font-medium rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 transition-colors">
                {label}
              </button>
            ))}
            <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white">
              <input type="date" value={startDate} onChange={e => setStart(e.target.value)}
                className="focus:outline-none bg-transparent text-slate-700 text-sm"/>
              <span className="text-slate-400 mx-1">-</span>
              <input type="date" value={endDate} onChange={e => setEnd(e.target.value)}
                className="focus:outline-none bg-transparent text-slate-700 text-sm"/>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-300 bg-white">
                {cols.map(c => (
                  <th key={c.key} className="text-left px-4 py-3 text-xs font-semibold text-slate-700 border border-slate-200 whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : !rows.length ? (
                <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-slate-400">No data for selected range</td></tr>
              ) : (
                <>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-b border-slate-200 hover:bg-slate-50/50">
                      {cols.map(c => (
                        <td key={c.key} className="px-4 py-3 text-slate-700 border border-slate-200 whitespace-nowrap">
                          {c.key === 'client'
                            ? row[c.key]
                            : c.amt
                              ? fmtAmt(row[c.key])
                              : fmtMet(row[c.key], metric)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {totals && (
                    <tr className="border-t-2 border-slate-300 bg-white font-semibold">
                      {cols.map(c => (
                        <td key={c.key} className="px-4 py-3 text-slate-800 border border-slate-200 whitespace-nowrap">
                          {c.key === 'client'
                            ? 'Total'
                            : c.amt
                              ? fmtAmt(totals[c.key])
                              : fmtMet(totals[c.key], metric)}
                        </td>
                      ))}
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <button onClick={downloadCSV}
            className="w-9 h-9 rounded border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50" title="Download CSV">
            <Download size={16}/>
          </button>
          <button onClick={() => window.print()}
            className="w-9 h-9 rounded border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50" title="Print">
            <Printer size={16}/>
          </button>
        </div>
      </div>
    </div>
  );
}
